import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ContentLocale } from '@/data/public-catalog'
import { microbiologyProducts, type MicrobiologyProductKey } from '@/data/product-pages/microbiology'
import { ProductMedia } from './ProductMedia'

type BodyProps = { language: ContentLocale }

function useItalianProduct(productKey: MicrobiologyProductKey) {
  const { t } = useTranslation('common')
  return (key: string, options?: Record<string, unknown>) => t(`productPage.${productKey}.${key}`, { lng: 'it', ...options })
}

function MediaSelector({
  productKey,
  media,
}: {
  productKey: MicrobiologyProductKey
  media: readonly string[]
}) {
  const { t } = useTranslation('common')
  const [selected, setSelected] = useState(0)
  return <div className={`micro-gallery micro-gallery--${productKey}`}>
    <ProductMedia path={media[selected]} altKey={`productPage.${productKey}.galleryAlt${selected + 1}`} className="micro-gallery-main" />
    <div className="micro-gallery-thumbnails" role="group" aria-label={t(`productPage.${productKey}.galleryLabel`)}>
      {media.map((path, index) => <button
        key={path}
        type="button"
        aria-pressed={selected === index}
        aria-label={t(`productPage.${productKey}.galleryChoice`, { index: index + 1 })}
        onClick={() => setSelected(index)}
      >
        <ProductMedia path={path} altKey={`productPage.${productKey}.galleryAlt${index + 1}`} />
      </button>)}
    </div>
  </div>
}

function FaNBody({ language }: BodyProps) {
  const text = useItalianProduct('faN')
  const paragraphs = text('paragraphs', { returnObjects: true }) as unknown as string[]
  const modules = text('modules', { returnObjects: true }) as unknown as string[]
  const media = microbiologyProducts['prd-mindray-fa-n-series'].gallery
  return <section className="shell-container product-section microbiology-body fan-body" aria-labelledby="fan-body-title">
    <div className="fan-copy product-copy" lang={language === 'en' ? 'it' : undefined}>
      <h2 id="fan-body-title">{text('bodyTitle')}</h2>
      {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
    </div>
    <section className="fan-family" aria-labelledby="fan-family-title" lang={language === 'en' ? 'it' : undefined}>
      <h2 id="fan-family-title">{text('familyTitle')}</h2>
      <ol>{modules.map(module => <li key={module}>{module}</li>)}</ol>
      <MediaSelector productKey="faN" media={media} />
    </section>
    <div className="fan-bottles product-copy" lang={language === 'en' ? 'it' : undefined}>
      <h2>{text('bottlesTitle')}</h2>
      <p>{text('bottles')}</p>
      <p>{text('fluids')}</p>
    </div>
  </section>
}

function Af300Body({ language }: BodyProps) {
  const text = useItalianProduct('af300')
  const instruments = text('instruments', { returnObjects: true }) as unknown as Array<{ title: string; description: string }>
  const panels = text('panels', { returnObjects: true }) as unknown as string[]
  const details = text('details', { returnObjects: true }) as unknown as string[]
  const media = microbiologyProducts['prd-mindray-af-300'].gallery
  return <section className="shell-container product-section microbiology-body af-body" aria-labelledby="af-body-title">
    <div className="af-intro product-copy" lang={language === 'en' ? 'it' : undefined}>
      <h2 id="af-body-title">{text('bodyTitle')}</h2>
      <p>{text('intro')}</p>
      <div className="af-instruments">{instruments.map(item => <section key={item.title}><h3>{item.title}</h3><p>{item.description}</p></section>)}</div>
    </div>
    <div className="af-panel-block" lang={language === 'en' ? 'it' : undefined}>
      <div><h2>{text('panelsTitle')}</h2><p>{text('panelsIntro')}</p><ul>{panels.map(panel => <li key={panel}>{panel}</li>)}</ul></div>
      <MediaSelector productKey="af300" media={media} />
    </div>
    <section className="af-analysis" aria-labelledby="af-analysis-title" lang={language === 'en' ? 'it' : undefined}>
      <div className="product-copy"><h2 id="af-analysis-title">{text('analysisTitle')}</h2>{details.map((detail, index) => <p key={index}>{detail}</p>)}</div>
    </section>
  </section>
}

export function MicrobiologyBody({ productKey, language }: { productKey: MicrobiologyProductKey; language: ContentLocale }) {
  return productKey === 'faN' ? <FaNBody language={language} /> : <Af300Body language={language} />
}
